import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, Inject, Input, PLATFORM_ID } from '@angular/core';
import { DeviceInfo } from '@innogy/shared/responsive-tools';
import { WINDOW } from '@innogy/utils-dom';
import isEqual from 'lodash/isEqual';

import type { MasonryContentItemModel } from '../masonry-content-item.model';

@Component({
  selector: 'wl-masonry-container[items][device]',
  templateUrl: './masonry-container.component.html',
  styleUrls: [],
})
export class MasonryContainerComponent implements OnInit, OnChanges {
  private readonly numberOfDesktopColumns = 3;
  private readonly numberOfTabletColumns = 2;
  private readonly numberOfMobileColumns = 1;
  public masonryItems: MasonryContentItemModel[][] = [];

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() isLoading = false;

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() items: MasonryContentItemModel[] = [];

  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() device?: DeviceInfo;

  ngOnInit(): void {
    this.masonryItems = this.splitMasonryIntoColumns(this.items, this.device);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const oldDevice = changes['device']?.previousValue;
    const newDevice = changes['device']?.currentValue;

    if (!isEqual(oldDevice, newDevice)) {
      this.masonryItems = this.splitMasonryIntoColumns(this.items, newDevice);
    }
  }

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    @Inject(PLATFORM_ID) readonly platformId: Object
  ) {}

  private getNumberOfColumns(deviceInfo?: DeviceInfo): number {
    if (!deviceInfo) {
      return this.numberOfDesktopColumns;
    }

    if (deviceInfo.isDesktop) {
      return this.numberOfDesktopColumns;
    }

    if (deviceInfo.isTablet) {
      return this.numberOfTabletColumns;
    }

    return this.numberOfMobileColumns;
  }

  private splitMasonryIntoColumns(
    items: MasonryContentItemModel[],
    deviceInfo?: DeviceInfo
  ): MasonryContentItemModel[][] {
    const numberOfColumns = this.getNumberOfColumns(deviceInfo);

    return this.splitContentItemsIntoMasonryColumns(numberOfColumns, items);
  }

  private splitContentItemsIntoMasonryColumns(
    numberOfColumns: number,
    contentItems?: MasonryContentItemModel[]
  ) {
    const updatedMasonryContent: MasonryContentItemModel[][] = new Array(
      numberOfColumns
    )
      .fill(null)
      .map((_) => []);

    if (Array.isArray(contentItems)) {
      contentItems.forEach((value: MasonryContentItemModel, index: number) => {
        const masonryColumnIndex = index % numberOfColumns;
        updatedMasonryContent[masonryColumnIndex].push(value);
      });
    }
    return updatedMasonryContent;
  }
}
